import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  TablePagination,
  InputAdornment,
  Breadcrumbs,
  Typography,
  Avatar,
} from "@mui/material";
import { MoreVert, Search } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AlertDialog from "../AlertDailog";
import { deleteUser, editUser, userList } from "../../apis/apisList/adminApi";
import { toast } from "react-toastify";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CustomLoader from "../CustomLoader";
import QuotationHistory from "../dashboard/QuotationHistory";
import { useSearchParams } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { formatDate } from "../../lib/formatDate";
import AddUserModal from "./AddUserModal";

const UserList = ({ setUserId, userId }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogType, setDialogType] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuUser, setMenuUser] = useState(null);
  const [isDelete, setIsDelete] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showPassword, setShowPassword] = useState(false);
  const [isUserModal, setIsUserModal] = useState(false);
  const [fetchTrigger, setFetchTrigger] = useState(false);
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const { results = [] } = await userList();
        setUsers(results);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadUsers();
  }, [fetchTrigger]);

  const handleMenuOpen = (event, user) => {
    setMenuAnchor(event.currentTarget);
    setMenuUser(user);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuUser(null);
  };

  const handleDialogOpen = (type) => {
    setSelectedUser(menuUser);
    setDialogType(type);
    handleMenuClose();
  };
  const handleDeleteUser = async () => {
    try {
      await deleteUser(menuUser._id);
      setUsers(users.filter((user) => user._id !== menuUser._id));
      setIsDelete(false);
      handleMenuClose();
      toast.success("User deleted successfully");
    } catch (error) {
      toast.error(error?.response?.data.message || "Error updating user");
      console.error("Error deleting user:", error);
      setIsDelete(false);
      handleMenuClose();
    }
  };
  const handleEditUser = async () => {
    try {
      await editUser(selectedUser._id, selectedUser);
      setUsers(
        users.map((u) => (u._id === selectedUser._id ? selectedUser : u))
      );
      setDialogType(null);
      setShowPassword(false);
      toast.success("User updated successfully");
    } catch (error) {
      toast.error(error?.response?.data.message || "Error updating user");
      console.error("Error updating user:", error);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleUserClick = (user) => {
    if (!user) return;
    setUserId(user);
  };

  const BreadcrumbComponent = () => {
    return (
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{ fontSize: "1rem", position: "relative", top: "40px" }}
        separator={<NavigateNextIcon fontSize="small" />}
      >
        <span
          style={{
            color: "#1976d2",
            cursor: "pointer",
          }}
          onClick={() => {
            setUserId(null);
          }}
        >
          User List
        </span>
        <Typography color="text.primary">Quotation</Typography>
        <Typography color="text.primary">
          {userId?.fullName || "User"}
        </Typography>
      </Breadcrumbs>
    );
  };

  const filteredUsers = users.filter(
    (user) =>
      user.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const cellStyle = {
    color: "#064776 !important",
    fontWeight: 600,
  };

  if (loading) return <CustomLoader loading={loading} />;
  if (error) return <div>Error: {error}</div>;
  return (
    <>
      {userId ? (
        <>
          <QuotationHistory
            userId={userId?._id}
            title={<BreadcrumbComponent />}
          />
        </>
      ) : (
        <>
          <div className="add_user_row">
            <TextField
              label="Search"
              variant="outlined"
              fullWidth
              margin="normal"
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{
                borderRadius: "10px",
                width: "250px",
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="disabled" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearchQuery("")} size="small">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              sx={{
                width: "150px",
              }}
              variant="contained"
              color="primary"
              onClick={() => {
                setIsUserModal(true);
              }}
            >
              Add User
            </Button>
          </div>

          <TableContainer
            component={Paper}
            sx={{ borderRadius: "10px", boxShadow: "none" }}
          >
            <Table
              sx={{
                width: "100%",
                "& thead": {
                  backgroundColor: "#EBEBEB",
                },
                "& tbody": {
                  backgroundColor: "#F8F8F8",
                },
                "& .MuiTableCell-root": {
                  borderBottom: `1px solid #E0E0E0`,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={cellStyle}>Avatar</TableCell>
                  <TableCell sx={cellStyle}>Name</TableCell>
                  <TableCell sx={cellStyle}>Email</TableCell>
                  <TableCell sx={cellStyle}>Phone</TableCell>
                  <TableCell sx={cellStyle}>Company</TableCell>
                  <TableCell sx={cellStyle}>Status</TableCell>
                  <TableCell sx={cellStyle}>Last Login</TableCell>
                  <TableCell sx={cellStyle}>Last Active</TableCell>
                  <TableCell sx={cellStyle}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length > 0 ? (
                  filteredUsers
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((user) => (
                      <TableRow
                        key={user._id}
                        onClick={() => handleUserClick(user)}
                        sx={{
                          cursor: "pointer",
                          "&:hover": {
                            bgcolor: "#80808014",
                          },
                        }}
                      >
                        <TableCell>
                          {<Avatar src={user.profilePic} />}
                        </TableCell>
                        <TableCell>{user.fullName || "N/A"}</TableCell>
                        <TableCell>{user.email || "N/A"}</TableCell>
                        <TableCell>{user.phone || "N/A"}</TableCell>
                        <TableCell>{user.company || "N/A"}</TableCell>
                        <TableCell>
                          <Chip
                            label={user.isActive ? "Active" : "Deleted"}
                            color={user.isActive ? "success" : "error"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          {user?.lastLogin ? formatDate(user?.lastLogin) : "-"}
                        </TableCell>
                        <TableCell align="center">
                          {user?.lastActive
                            ? formatDate(user?.lastActive)
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleMenuOpen(e, user);
                            }}
                          >
                            <MoreVert />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8}>No users found.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
          {/* Action Menu */}
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
          >
            <MenuItem
              onClick={() => handleDialogOpen("view")}
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              View
              <ListItemIcon>
                <VisibilityIcon />
              </ListItemIcon>
            </MenuItem>
            <MenuItem
              onClick={() => handleDialogOpen("edit")}
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              Edit
              <ListItemIcon>
                <EditIcon />
              </ListItemIcon>
            </MenuItem>
            <MenuItem
              onClick={(e) => {
                setIsDelete(true);
              }}
              sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}
            >
              Delete
              <ListItemIcon>
                <DeleteIcon />
              </ListItemIcon>
            </MenuItem>
          </Menu>

          {/* View/Edit Dialog */}
          <Dialog
            open={Boolean(dialogType)}
            onClose={() => setDialogType(null)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              {dialogType === "view" ? "User Details" : "Edit User"}
            </DialogTitle>
            <DialogContent>
              {selectedUser &&
                (dialogType === "view" ? (
                  <>
                    <p>
                      <strong>Name:</strong> {selectedUser.fullName}
                    </p>
                    <p>
                      <strong>Email:</strong> {selectedUser.email}
                    </p>
                    <p>
                      <strong>Phone:</strong> {selectedUser.phone}
                    </p>
                    <p>
                      <strong>Company:</strong> {selectedUser.company}
                    </p>
                    <p>
                      <strong>StreetAddress:</strong>{" "}
                      {selectedUser?.streetAddress || "N/A"}
                    </p>
                    <p>
                      <strong>State:</strong> {selectedUser?.state || "N/A"}
                    </p>
                    <p>
                      <strong>Country:</strong> {selectedUser?.country || "N/A"}
                    </p>
                    <p>
                      <strong>PostalCode:</strong>{" "}
                      {selectedUser?.postalCode || "N/A"}
                    </p>
                  </>
                ) : (
                  <>
                    <TextField
                      fullWidth
                      margin="dense"
                      label="Name"
                      value={selectedUser.fullName}
                      onChange={(e) =>
                        setSelectedUser({
                          ...selectedUser,
                          fullName: e.target.value,
                        })
                      }
                    />
                    <TextField
                      fullWidth
                      margin="dense"
                      label="Email"
                      value={selectedUser.email}
                      onChange={(e) =>
                        setSelectedUser({
                          ...selectedUser,
                          email: e.target.value,
                        })
                      }
                    />
                    <TextField
                      fullWidth
                      margin="dense"
                      label="Password"
                      type={showPassword ? "text" : "password"}
                      value={selectedUser.password}
                      onChange={(e) =>
                        setSelectedUser({
                          ...selectedUser,
                          password: e.target.value,
                        })
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      fullWidth
                      margin="dense"
                      label="Phone"
                      value={selectedUser.phone}
                      onChange={(e) =>
                        setSelectedUser({
                          ...selectedUser,
                          phone: e.target.value,
                        })
                      }
                    />
                    <TextField
                      fullWidth
                      margin="dense"
                      label="Company"
                      value={selectedUser.company}
                      onChange={(e) =>
                        setSelectedUser({
                          ...selectedUser,
                          company: e.target.value,
                        })
                      }
                    />
                  </>
                ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogType(null)} color="primary">
                Cancel
              </Button>
              {dialogType === "edit" && (
                <Button onClick={handleEditUser} color="primary">
                  Save
                </Button>
              )}
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          {isDelete && (
            <AlertDialog
              open={isDelete}
              onClose={() => setIsDelete(false)}
              title="Confirm User Deletion"
              description="Are you sure you want to delete this user? This action cannot be undone, and the user's data will be permanently removed."
              onSubmit={() => handleDeleteUser()}
              submitText="Delete"
              cancelText="Cancel"
            />
          )}
          <AddUserModal
            open={isUserModal}
            onClose={() => setIsUserModal(false)}
            fetchTrigger={fetchTrigger}
            setFetchTrigger={setFetchTrigger}
          />
        </>
      )}
    </>
  );
};

export default UserList;
